import { GetCurrentTabCommand } from 'constants/BackgroundScript';
import { Maybe, Undef } from 'types/UtilityTypes';

export type GetCurrentTabOptions = {
  /**
   * Resolve the tab the side panel was opened from (service worker), falling back to
   * the active tab in the current window. Matches background `getCurrentTab` in
   * `src/pages/background/sidepanel-opener/popup.ts`.
   */
  preferSidepanelHostTab?: boolean;
};

/**
 * Get the current tab from the active window. To be used when Drumkit is running as a
 * [Chromium side panel](https://developer.chrome.com/docs/extensions/reference/api/sidePanel).
 * @returns The current tab or undefined if chrome is not available
 *
 * NOTE: Because a user can have multiple tabs open and a sidepanel open on each one,
 * and we need conditional rendering based on the current webpage, we cannot attach a listener to all the sidepanel ports.
 * Instead, we check the current tab the currently running sidepanel is attached to on each request.
 * Furthermore, the tabId may not change but the URL does, hence why this Tab object is not attached to SidebarStateContext.
 */
export async function getCurrentTab(
  options?: GetCurrentTabOptions
): Promise<Undef<chrome.tabs.Tab>> {
  if (options?.preferSidepanelHostTab) {
    if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
      return undefined;
    }
    try {
      const tab = await chrome.runtime.sendMessage({
        command: GetCurrentTabCommand,
        preferSidepanelHostTab: true,
      });
      return tab as Undef<chrome.tabs.Tab>;
    } catch {
      // Service worker may be inactive or extension context invalidated.
      return undefined;
    }
  }
  if (typeof chrome === 'undefined' || !chrome.tabs) {
    return undefined;
  }
  const [tab] = await chrome.tabs.query({ active: true, currentWindow: true });
  return tab;
}

/**
 * Gets the HTML content of a tab by injecting a content script.
 * @param tabId The ID of the tab to get HTML from
 * @param allFrames Whether to include HTML from child frames
 * @returns A promise that resolves to the tab's HTML content
 */
export async function getTabHTML(
  tabId: number,
  allFrames = false
): Promise<Maybe<string>> {
  return new Promise((resolve) => {
    if (typeof chrome === 'undefined' || chrome?.runtime === undefined) {
      resolve(null);
    }
    const listener = (message: any) => {
      if (message.action === 'getTabHTMLResponse' && message?.tabId === tabId) {
        chrome.runtime.onMessage.removeListener(listener);
        resolve(message.html);
      }
    };

    chrome.runtime.onMessage.addListener(listener);

    const target: chrome.scripting.InjectionTarget = allFrames
      ? { tabId, allFrames: true }
      : { tabId };

    const getHTML = allFrames
      ? () => document.body?.innerHTML ?? document.documentElement.innerHTML
      : () => document.documentElement.outerHTML;

    chrome.scripting
      .executeScript({
        target,
        func: getHTML,
      })
      .then((results) => {
        const html = allFrames
          ? results
              .map((result) => result.result)
              .filter((result): result is string => typeof result === 'string')
              .join('\n')
          : results?.[0]?.result;

        chrome.runtime.onMessage.removeListener(listener);
        resolve(html || null);
      })
      .catch((error) => {
        console.error('Error getting tab HTML:', error);
        chrome.runtime.onMessage.removeListener(listener);
        resolve(null);
      });
  });
}
